import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { StoreSummaryDto } from '@bhops/shared';
import { storesApi } from '@/api/stores';
import { useDebouncedValue } from './useDebouncedValue';

// StorePicker — search-on-type combo box, scoped by customerId.
// Returns a StoreSummaryDto via `onSelect` (or null if `allowNone`
// is true and the user picks "(매장 미지정)").
//
// Disabled state when `customerId` is null — placeholder swaps to
// "고객사 먼저 선택하세요". Mirrors the CustomerPicker pattern.

interface Props {
  /**
   * Customer this store belongs to. Picker is disabled until
   * non-null. Caller usually wires this from an adjacent
   * CustomerPicker.
   */
  customerId: string | null;
  selected: string | null;
  onSelect: (store: StoreSummaryDto | null) => void;
  /**
   * Show "(매장 미지정)" option that calls `onSelect(null)`.
   * Required for Deal create where storeId is optional.
   */
  allowNone?: boolean;
  placeholder?: string;
  /**
   * Round 2 Phase F (#8) — true 면 "배정 가능"(계약 완료 ~ 설치 완료) 고객사의
   * 매장만 검색한다(고객사 없는 매장 제외). 기본 false. 두 StorePicker 사본이
   * 동일 옵션을 갖도록 추가 — 현재는 배치 모달에서만 사용.
   */
  assignableOnly?: boolean;
}

export function StorePicker({
  customerId,
  selected,
  onSelect,
  allowNone = false,
  placeholder = '매장 검색',
  assignableOnly = false,
}: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const debounced = useDebouncedValue(query, 250);
  const containerRef = useRef<HTMLDivElement>(null);

  const disabled = customerId === null;

  const { data, isLoading } = useQuery({
    queryKey: ['stores', 'picker', customerId, assignableOnly, debounced],
    queryFn: () =>
      storesApi.list({
        customerId: customerId ?? undefined,
        search: debounced || undefined,
        pageSize: 20,
        ...(assignableOnly ? { assignableOnly: true } : {}),
      }),
    enabled: open && !disabled,
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  // The "(매장 미지정)" pseudo-row sits at index 0 when allowNone
  // is true. Other items shift by 1 for keyboard nav.
  const totalCount = items.length + (allowNone ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    function onClickAway(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClickAway);
    return () => window.removeEventListener('mousedown', onClickAway);
  }, [open]);

  function pick(idx: number): void {
    if (allowNone && idx === 0) {
      onSelect(null);
      setQuery('(매장 미지정)');
      setOpen(false);
      return;
    }
    const itemIdx = allowNone ? idx - 1 : idx;
    const store = items[itemIdx];
    if (!store) return;
    onSelect(store);
    setQuery(store.branchName ? `${store.name} · ${store.branchName}` : store.name);
    setOpen(false);
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      setHighlightIdx((i) => Math.min(totalCount - 1, i + 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightIdx((i) => Math.max(0, i - 1));
      e.preventDefault();
    } else if (e.key === 'Enter') {
      pick(highlightIdx);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} style={wrap}>
      <input
        type="text"
        value={query}
        placeholder={disabled ? '고객사를 먼저 골라 주세요' : placeholder}
        disabled={disabled}
        onFocus={() => !disabled && setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlightIdx(0);
        }}
        onKeyDown={handleKey}
        style={inputStyle(disabled)}
      />
      {open && !disabled && (
        <div style={dropdown}>
          {allowNone && (
            <div
              role="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(0);
              }}
              onMouseEnter={() => setHighlightIdx(0)}
              style={{
                ...rowStyle(highlightIdx === 0, selected === null),
                fontStyle: 'italic',
                color: 'var(--fg-muted)',
              }}
            >
              (매장 미지정)
            </div>
          )}
          {isLoading && (
            <div style={loadingRow} className="kuun-caption">
              불러오는 중…
            </div>
          )}
          {!isLoading && items.length === 0 && !allowNone && (
            <div style={loadingRow} className="kuun-caption">
              결과 없음
            </div>
          )}
          {items.map((s, i) => {
            const idx = allowNone ? i + 1 : i;
            return (
              <div
                key={s.id}
                role="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(idx);
                }}
                onMouseEnter={() => setHighlightIdx(idx)}
                style={rowStyle(idx === highlightIdx, s.id === selected)}
              >
                <span style={{ fontWeight: 500 }}>{s.name}</span>
                {s.branchName && <span style={{ color: 'var(--fg-muted)' }}>· {s.branchName}</span>}
                <span
                  className="kuun-caption"
                  style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}
                >
                  {[s.city, s.region].filter(Boolean).join(' · ')}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const wrap: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: '100%',
};

const inputStyle = (disabled: boolean): CSSProperties => ({
  width: '100%',
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: disabled ? 'var(--bg-sunken)' : 'var(--bg-surface)',
  fontSize: 13,
  color: disabled ? 'var(--fg-muted)' : 'var(--fg-default)',
});

const dropdown: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  boxShadow: 'var(--shadow-md)',
  maxHeight: 320,
  overflowY: 'auto',
  zIndex: 100,
};

const loadingRow: CSSProperties = {
  padding: '10px 12px',
  textAlign: 'center',
};

const rowStyle = (highlighted: boolean, selected: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: 13,
  background: highlighted ? 'var(--blue-50)' : 'transparent',
  fontWeight: selected ? 500 : 400,
  borderLeft: selected ? '3px solid var(--blue-500)' : '3px solid transparent',
});
